<template>
  <div class="p-4">
    <!-- Header con navegación -->
    <div class="mb-4">
      <button @click="router.push('/pedidos')" class="text-violet-600 hover:text-violet-700 dark:text-violet-400 flex items-center mb-2 text-sm">
        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
        </svg>
        Volver a Pedidos
      </button>
      <div class="flex justify-between items-start">
        <div>
          <h1 class="text-xl font-bold text-gray-900 dark:text-white">{{ pedido?.numero_pedido || 'Cargando...' }}</h1>
          <p class="text-sm text-gray-600 dark:text-gray-400">Detalles del pedido</p>
        </div>
        <div v-if="pedido" class="flex gap-2 flex-wrap">
          <!-- Botón: Agregar Cotizaciones (solo en borrador) -->
          <button
            v-if="pedido.estado === 'borrador'"
            @click="mostrarModalCotizaciones"
            class="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm flex items-center gap-2"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            Agregar Cotizaciones
          </button>

          <!-- Botón: Confirmar Pedido (solo en borrador con items) -->
          <button
            v-if="pedido.estado === 'borrador' && pedido.items_count > 0"
            @click="confirmarPedido"
            class="px-3 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 text-sm flex items-center gap-2"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            Confirmar Pedido
          </button>

          <!-- Botón: Crear Orden de Producción (confirmado, sin orden) -->
          <button
            v-if="!pedido.orden_produccion_id && pedido.estado !== 'cancelado' && pedido.estado !== 'borrador'"
            @click="crearOrdenProduccion"
            class="px-3 py-2 bg-violet-600 text-white rounded-md hover:bg-violet-700 text-sm flex items-center gap-2"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
            </svg>
            Crear Orden de Producción
          </button>

          <!-- Botón: Cambiar Estado -->
          <button
            v-if="pedido.estado !== 'cancelado' && pedido.estado !== 'entregado' && pedido.estado !== 'borrador'"
            @click="cambiarEstado"
            class="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
          >
            Cambiar Estado
          </button>
        </div>
      </div>
    </div>

    <div v-if="error" class="text-red-500 text-center mb-4 text-sm">{{ error }}</div>
    <div v-if="loading" class="text-center py-8 text-gray-500 text-sm">Cargando detalles del pedido...</div>

    <div v-if="pedido && !loading" class="space-y-4">
      <!-- Información Principal -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <!-- Card: Información del Pedido -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <h2 class="text-md font-semibold text-gray-900 dark:text-white mb-3">Información del Pedido</h2>
          <div class="space-y-2 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Número:</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ pedido.numero_pedido }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Estado:</span>
              <span :class="getEstadoBadgeClass(pedido.estado)" class="px-2 py-0.5 rounded-full text-xs font-medium">
                {{ getEstadoText(pedido.estado) }}
              </span>
            </div>
            <div v-if="pedido.folio_cliente" class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Folio Cliente:</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ pedido.folio_cliente }}</span>
            </div>
            <div v-if="pedido.referencia" class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Referencia:</span>
              <span class="text-gray-900 dark:text-white">{{ pedido.referencia }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Cotización:</span>
              <router-link
                v-if="pedido.cotizacion"
                :to="`/cotizaciones/${pedido.cotizacion_id}`"
                class="font-medium text-violet-600 hover:text-violet-700 dark:text-violet-400"
              >
                {{ pedido.cotizacion.numero_cotizacion }}
              </router-link>
              <span v-else class="text-gray-500">N/A</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Fecha Pedido:</span>
              <span class="text-gray-900 dark:text-white">{{ formatDate(pedido.fecha_pedido) }}</span>
            </div>
            <div v-if="pedido.fecha_entrega_requerida" class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Entrega Requerida:</span>
              <span class="text-orange-600 dark:text-orange-400 font-medium">{{ formatDate(pedido.fecha_entrega_requerida) }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Entrega Estimada:</span>
              <span class="text-gray-900 dark:text-white">{{ formatDate(pedido.fecha_entrega_estimada) }}</span>
            </div>
            <div v-if="pedido.fecha_entrega_real" class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Entrega Real:</span>
              <span class="text-green-600 dark:text-green-400 font-medium">{{ formatDate(pedido.fecha_entrega_real) }}</span>
            </div>
          </div>
        </div>

        <!-- Card: Cliente -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <h2 class="text-md font-semibold text-gray-900 dark:text-white mb-3">Cliente</h2>
          <div v-if="pedido.contacto" class="space-y-2 text-sm">
            <div>
              <span class="text-gray-600 dark:text-gray-400">Nombre:</span>
              <p class="font-medium text-gray-900 dark:text-white">{{ getContactoNombre(pedido.contacto) }}</p>
            </div>
            <div v-if="pedido.contacto.email">
              <span class="text-gray-600 dark:text-gray-400">Email:</span>
              <p class="text-gray-900 dark:text-white">{{ pedido.contacto.email }}</p>
            </div>
            <div v-if="pedido.contacto.telefono">
              <span class="text-gray-600 dark:text-gray-400">Teléfono:</span>
              <p class="text-gray-900 dark:text-white">{{ pedido.contacto.telefono }}</p>
            </div>
          </div>
          <div v-else class="text-gray-500 text-sm">Sin información de cliente</div>
        </div>

        <!-- Card: Totales -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <h2 class="text-md font-semibold text-gray-900 dark:text-white mb-3">Totales</h2>
          <div class="space-y-2 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Subtotal:</span>
              <span class="text-gray-900 dark:text-white">${{ parseFloat(pedido.subtotal || 0).toFixed(2) }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Impuestos (16%):</span>
              <span class="text-gray-900 dark:text-white">${{ parseFloat(pedido.impuestos || 0).toFixed(2) }}</span>
            </div>
            <div v-if="pedido.descuento > 0" class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Descuento:</span>
              <span class="text-red-600 dark:text-red-400">-${{ parseFloat(pedido.descuento || 0).toFixed(2) }}</span>
            </div>
            <div class="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
              <span class="font-semibold text-gray-900 dark:text-white">Total:</span>
              <span class="font-bold text-green-600 dark:text-green-400 text-lg">${{ parseFloat(pedido.total || 0).toFixed(2) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Items del Pedido -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <h2 class="text-md font-semibold text-gray-900 dark:text-white mb-3">Productos del Pedido</h2>
        <div class="overflow-x-auto">
          <table class="w-full text-xs text-left text-gray-500 dark:text-gray-400">
            <thead class="text-xs uppercase bg-gray-100 dark:bg-gray-800 dark:text-gray-300 border-b-2 border-gray-200 dark:border-gray-700">
              <tr>
                <th class="px-3 py-2 font-bold">#</th>
                <th class="px-3 py-2 font-bold">Producto</th>
                <th class="px-3 py-2 font-bold">Folio</th>
                <th class="px-3 py-2 font-bold text-center">Cantidad</th>
                <th class="px-3 py-2 font-bold text-right">Precio Unit.</th>
                <th class="px-3 py-2 font-bold text-right">Subtotal</th>
                <th class="px-3 py-2 font-bold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in pedido.items" :key="item.id" class="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                <td class="px-3 py-2 text-gray-900 dark:text-white">{{ index + 1 }}</td>
                <td class="px-3 py-2">
                  <div class="text-gray-900 dark:text-white font-medium">
                    {{ item.producto?.nombre || 'Producto sin nombre' }}
                  </div>
                  <div v-if="item.producto?.prototipo" class="text-gray-500 text-xs">
                    Prototipo: {{ item.producto.prototipo.folio }}
                  </div>
                </td>
                <td class="px-3 py-2 text-gray-700 dark:text-gray-300">
                  {{ item.producto?.folio || 'N/A' }}
                </td>
                <td class="px-3 py-2 text-center">
                  <span class="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full dark:bg-blue-900 dark:text-blue-300">
                    {{ item.cantidad }}
                  </span>
                </td>
                <td class="px-3 py-2 text-right text-gray-900 dark:text-white">
                  ${{ parseFloat(item.precio_unitario || 0).toFixed(2) }}
                </td>
                <td class="px-3 py-2 text-right font-medium text-green-600 dark:text-green-400">
                  ${{ parseFloat(item.subtotal || 0).toFixed(2) }}
                </td>
                <td class="px-3 py-2">
                  <button
                    v-if="item.producto"
                    @click="verProducto(item.producto)"
                    class="text-violet-500 hover:text-violet-700 text-xs"
                    title="Ver producto"
                  >
                    Ver detalles
                  </button>
                </td>
              </tr>
              <tr v-if="!pedido.items || pedido.items.length === 0">
                <td colspan="7" class="text-center py-6 text-gray-400 text-sm">
                  No hay productos en este pedido.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Notas y Notas de Producción -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <!-- Notas Generales -->
        <div v-if="pedido.notas" class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <h2 class="text-md font-semibold text-gray-900 dark:text-white mb-2">Notas</h2>
          <p class="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{{ pedido.notas }}</p>
        </div>

        <!-- Notas de Producción -->
        <div v-if="pedido.notas_produccion" class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <h2 class="text-md font-semibold text-gray-900 dark:text-white mb-2">Notas de Producción</h2>
          <p class="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{{ pedido.notas_produccion }}</p>
        </div>
      </div>

      <!-- Orden de Producción (si existe) -->
      <div v-if="pedido.orden_produccion_id" class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <h2 class="text-md font-semibold text-gray-900 dark:text-white mb-3">Orden de Producción</h2>
        <div v-if="pedido.orden_produccion" class="text-sm space-y-2">
          <div class="flex justify-between">
            <span class="text-gray-600 dark:text-gray-400">Número:</span>
            <router-link
              :to="`/produccion/ordenes/${pedido.orden_produccion.id}`"
              class="font-medium text-violet-600 hover:text-violet-700 dark:text-violet-400"
            >
              {{ pedido.orden_produccion.numero_orden }}
            </router-link>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600 dark:text-gray-400">Estado:</span>
            <span class="text-gray-900 dark:text-white capitalize">{{ pedido.orden_produccion.estado }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600 dark:text-gray-400">Fecha:</span>
            <span class="text-gray-900 dark:text-white">{{ formatDate(pedido.orden_produccion.fecha_orden) }}</span>
          </div>
        </div>
        <div v-else class="text-sm text-gray-500 dark:text-gray-400">
          Orden de producción asociada (ID: {{ pedido.orden_produccion_id }})
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useHttp } from '@/composables/useHttp'
import { useNotifications } from '@/composables/useNotifications'
import Swal from 'sweetalert2'

const router = useRouter()
const route = useRoute()
const { get, post, loading, error, data } = useHttp()
const { showSuccess, showError } = useNotifications()

const pedido = ref(null)

// Cargar pedido
const fetchPedido = async () => {
  try {
    await get(`pedidos/${route.params.id}`)
    if (data.value) {
      pedido.value = data.value
    }
  } catch (err) {
    showError('Error al cargar el pedido')
  }
}

// Cambiar estado
const cambiarEstado = async () => {
  const estados = [
    { value: 'borrador', label: 'Borrador' },
    { value: 'confirmado', label: 'Confirmado' },
    { value: 'en_produccion', label: 'En Producción' },
    { value: 'completado', label: 'Completado' },
    { value: 'entregado', label: 'Entregado' },
    { value: 'cancelado', label: 'Cancelado' }
  ]

  const { value: nuevoEstado } = await Swal.fire({
    title: 'Cambiar Estado del Pedido',
    text: `Pedido: ${pedido.value.numero_pedido}`,
    input: 'select',
    inputOptions: Object.fromEntries(estados.map(e => [e.value, e.label])),
    inputValue: pedido.value.estado,
    showCancelButton: true,
    confirmButtonColor: '#8b5cf6',
    cancelButtonColor: '#6b7280',
    confirmButtonText: 'Cambiar',
    cancelButtonText: 'Cancelar',
    inputValidator: (value) => {
      if (!value) {
        return 'Debes seleccionar un estado'
      }
    }
  })

  if (nuevoEstado && nuevoEstado !== pedido.value.estado) {
    try {
      await post(`pedidos/${pedido.value.id}/cambiar-estado`, { estado: nuevoEstado })
      await fetchPedido()
      showSuccess('Estado actualizado exitosamente')
    } catch (err) {
      showError('No se pudo cambiar el estado')
    }
  }
}

// Crear orden de producción
const crearOrdenProduccion = async () => {
  const { isConfirmed } = await Swal.fire({
    title: '¿Crear Orden de Producción?',
    html: `
      <p>¿Deseas crear una orden de producción para el pedido <strong>${pedido.value.numero_pedido}</strong>?</p>
      <p class="text-sm text-gray-600 mt-2">Esto creará una nueva orden con todos los productos del pedido.</p>
    `,
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#8b5cf6',
    cancelButtonColor: '#6b7280',
    confirmButtonText: 'Sí, crear',
    cancelButtonText: 'Cancelar'
  })

  if (isConfirmed) {
    try {
      await post(`pedidos/${pedido.value.id}/crear-orden-produccion`)

      if (data.value) {
        showSuccess('Orden de producción creada exitosamente')

        // Preguntar si desea ver la orden creada
        const { isConfirmed: verOrden } = await Swal.fire({
          title: 'Orden Creada',
          text: `Se creó la orden ${data.value.orden_produccion?.numero_orden || ''}. ¿Deseas verla ahora?`,
          icon: 'success',
          showCancelButton: true,
          confirmButtonColor: '#8b5cf6',
          cancelButtonColor: '#6b7280',
          confirmButtonText: 'Ver orden',
          cancelButtonText: 'Quedarme aquí'
        })

        if (verOrden && data.value.orden_produccion) {
          router.push(`/produccion/ordenes/${data.value.orden_produccion.id}`)
        } else {
          await fetchPedido()
        }
      }
    } catch (err) {
      showError('No se pudo crear la orden de producción')
    }
  }
}

// Mostrar modal para agregar cotizaciones
const mostrarModalCotizaciones = async () => {
  try {
    // Obtener cotizaciones disponibles
    await get(`pedidos/${pedido.value.id}/cotizaciones-disponibles`)

    if (!data.value || data.value.length === 0) {
      Swal.fire({
        title: 'Sin cotizaciones',
        text: 'No hay cotizaciones aprobadas disponibles para este cliente.',
        icon: 'info',
        confirmButtonColor: '#8b5cf6'
      })
      return
    }

    // Crear opciones para el select
    const opciones = {}
    data.value.forEach(cot => {
      opciones[cot.id] = `${cot.numero_cotizacion} - ${cot.items_count} item(s) - $${parseFloat(cot.total || 0).toFixed(2)}`
    })

    const { value: cotizacionIds } = await Swal.fire({
      title: 'Seleccionar Cotizaciones',
      html: `
        <p class="text-sm text-gray-600 mb-4">Selecciona las cotizaciones que deseas agregar a este pedido:</p>
        <div id="cotizaciones-list" class="text-left space-y-2"></div>
      `,
      didOpen: () => {
        const container = document.getElementById('cotizaciones-list')
        data.value.forEach(cot => {
          const div = document.createElement('div')
          div.className = 'flex items-start gap-2 p-2 border rounded'
          div.innerHTML = `
            <input type="checkbox" id="cot-${cot.id}" value="${cot.id}" class="mt-1">
            <label for="cot-${cot.id}" class="text-sm cursor-pointer">
              <strong>${cot.numero_cotizacion}</strong><br>
              ${cot.items_count} item(s) - Total: $${parseFloat(cot.total || 0).toFixed(2)}
            </label>
          `
          container.appendChild(div)
        })
      },
      showCancelButton: true,
      confirmButtonColor: '#8b5cf6',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Agregar',
      cancelButtonText: 'Cancelar',
      preConfirm: () => {
        const checkboxes = document.querySelectorAll('#cotizaciones-list input[type="checkbox"]:checked')
        const ids = Array.from(checkboxes).map(cb => parseInt(cb.value))
        if (ids.length === 0) {
          Swal.showValidationMessage('Debes seleccionar al menos una cotización')
        }
        return ids
      }
    })

    if (cotizacionIds && cotizacionIds.length > 0) {
      await post(`pedidos/${pedido.value.id}/agregar-cotizaciones`, { cotizacion_ids: cotizacionIds })
      showSuccess(`${cotizacionIds.length} cotización(es) agregada(s) exitosamente`)
      await fetchPedido()
    }
  } catch (err) {
    showError('Error al agregar cotizaciones')
  }
}

// Confirmar pedido
const confirmarPedido = async () => {
  const { isConfirmed } = await Swal.fire({
    title: '¿Confirmar Pedido?',
    html: `
      <p>¿Deseas confirmar el pedido <strong>${pedido.value.numero_pedido}</strong>?</p>
      <p class="text-sm text-gray-600 mt-2">El pedido pasará de borrador a confirmado y no podrás agregar más cotizaciones.</p>
    `,
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#ea580c',
    cancelButtonColor: '#6b7280',
    confirmButtonText: 'Sí, confirmar',
    cancelButtonText: 'Cancelar'
  })

  if (isConfirmed) {
    try {
      await post(`pedidos/${pedido.value.id}/confirmar`)
      showSuccess('Pedido confirmado exitosamente')
      await fetchPedido()
    } catch (err) {
      showError('No se pudo confirmar el pedido')
    }
  }
}

// Ver detalles de producto
const verProducto = (producto) => {
  Swal.fire({
    title: producto.folio,
    html: `
      <div class="text-left space-y-2 text-sm">
        <p><strong>Nombre:</strong> ${producto.nombre || 'N/A'}</p>
        <p><strong>Descripción:</strong> ${producto.descripcion || 'N/A'}</p>
        <p><strong>Costo Unitario:</strong> $${parseFloat(producto.costoUnitario || 0).toFixed(2)}</p>
        <p><strong>Precio Final:</strong> $${parseFloat(producto.precioVentaFinal || 0).toFixed(2)}</p>
        ${producto.prototipo ? `<p><strong>Prototipo:</strong> ${producto.prototipo.folio}</p>` : ''}
      </div>
    `,
    confirmButtonColor: '#8b5cf6',
    confirmButtonText: 'Cerrar'
  })
}

// Utilidades
const getContactoNombre = (contacto) => {
  if (!contacto) return 'Sin asignar'
  return contacto.razon_social || `${contacto.nombre || ''} ${contacto.apellido_paterno || ''}`.trim() || 'Sin nombre'
}

const getEstadoBadgeClass = (estado) => {
  const classes = {
    'borrador': 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
    'confirmado': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    'en_produccion': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    'completado': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    'entregado': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
    'cancelado': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
  }
  return classes[estado] || classes.borrador
}

const getEstadoText = (estado) => {
  const texts = {
    'borrador': 'Borrador',
    'confirmado': 'Confirmado',
    'en_produccion': 'En Producción',
    'completado': 'Completado',
    'entregado': 'Entregado',
    'cancelado': 'Cancelado'
  }
  return texts[estado] || 'Borrador'
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

onMounted(() => {
  fetchPedido()
})
</script>
